/**
 * SiteConnectionCard — live visibility on what's actually wired between
 * the ecosystem CMS and the deployed public site.
 *
 * For a given `slug` (abmhc | fhbf) it:
 *   1. Reads/edits the `site.public_url` field via the caller's setField
 *      (so the URL is stored on the SAME CMS doc as everything else)
 *   2. Renders a live iframe preview of that URL
 *   3. Calls `GET /api/admin/site-connection/{slug}` to server-side probe
 *      whether the embed script is present and whether the CMS text is
 *      rendered on the live page
 *   4. Shows the raw JSON payload the ecosystem is currently serving to
 *      that site — the exact "source of truth"
 */
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Link2, RefreshCw, ExternalLink, CheckCircle2, XCircle, AlertTriangle, Code, Globe } from 'lucide-react';
import { getToken } from '@/lib/authToken';

const API = process.env.REACT_APP_BACKEND_URL;
const authH = () => ({ Authorization: `Bearer ${getToken()}` });

// Maps slug → public content endpoint the widget/PHP consumes.
const PUBLIC_ENDPOINTS = {
  abmhc: '/api/v1/sites/abmhc/public',
  fhbf:  '/api/v1/fhbf/fhbf/public',
};

export default function SiteConnectionCard({ slug, publicUrl, onUrlChange, brandColor = 'emerald' }) {
  const [status, setStatus] = useState(null);
  const [payload, setPayload] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPayload, setShowPayload] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [localUrl, setLocalUrl] = useState(publicUrl || '');

  useEffect(() => { setLocalUrl(publicUrl || ''); }, [publicUrl]);

  const check = useCallback(async () => {
    setBusy(true);
    try {
      const r = await axios.get(`${API}/api/admin/site-connection/${slug}`,
                                  { headers: authH() });
      setStatus(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Connection check failed');
    } finally { setBusy(false); }
  }, [slug]);

  const fetchPayload = useCallback(async () => {
    try {
      const r = await axios.get(`${API}${PUBLIC_ENDPOINTS[slug]}`);
      setPayload(r.data);
      setShowPayload(true);
    } catch (e) {
      toast.error('Failed to fetch current CMS payload');
    }
  }, [slug]);

  useEffect(() => { if (slug) check(); }, [slug, check]);

  const commitUrl = () => {
    if (localUrl.trim() !== (publicUrl || '').trim()) {
      onUrlChange && onUrlChange(localUrl.trim());
      toast.info('Public URL updated locally — click Save at the top to persist.');
    }
  };

  const borderCls = `border-${brandColor}-500/30`;

  return (
    <Card className={`bg-slate-900/60 ${borderCls}`} data-testid={`site-connection-card-${slug}`}>
      <CardHeader>
        <CardTitle className="text-sm text-white flex items-center gap-2">
          <StatusIcon status={status} /> Live site connection · <StatusBadge status={status} />
        </CardTitle>
        <CardDescription className="text-slate-400">
          {status?.message
            || 'Point this at your deployed one.com site to see it live and detect whether the ecosystem embed is wired in.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* URL config */}
        <div className="grid md:grid-cols-[1fr_auto_auto_auto] gap-2 items-end">
          <div>
            <Label className="text-xs text-slate-400">Public site URL (production)</Label>
            <Input value={localUrl}
                   onChange={(e) => setLocalUrl(e.target.value)}
                   onBlur={commitUrl}
                   className="bg-slate-900 border-slate-800 mt-1"
                   placeholder="https://your-site.com"
                   data-testid={`site-connection-url-${slug}`} />
          </div>
          <Button variant="outline" onClick={check} disabled={busy}
                  className="border-slate-700 text-slate-300"
                  data-testid={`site-connection-check-${slug}`}>
            <RefreshCw className={`h-4 w-4 mr-1 ${busy ? 'animate-spin' : ''}`} />
            Re-check
          </Button>
          <Button variant="outline" onClick={fetchPayload}
                  className="border-slate-700 text-slate-300"
                  data-testid={`site-connection-payload-${slug}`}>
            <Code className="h-4 w-4 mr-1" /> Current payload
          </Button>
          <Button variant="outline" disabled={!localUrl}
                  onClick={() => window.open(localUrl, '_blank', 'noreferrer')}
                  className="border-slate-700 text-slate-300"
                  data-testid={`site-connection-open-${slug}`}>
            <ExternalLink className="h-4 w-4 mr-1" /> Open
          </Button>
        </div>

        {/* Status details */}
        {status && status.public_url && (
          <div className="grid md:grid-cols-4 gap-2 text-xs">
            <StatusPill label="Reachable" ok={status.reachable} />
            <StatusPill label={`HTTP ${status.status ?? '—'}`}
                        ok={status.status && status.status < 400} />
            <StatusPill label="Embed script" ok={status.embed_detected} />
            <StatusPill label="CMS content" ok={status.cms_content_detected} />
          </div>
        )}

        {/* Iframe preview */}
        {localUrl ? (
          <div className="rounded overflow-hidden border border-slate-800 bg-slate-950"
               data-testid={`site-connection-iframe-${slug}`}>
            <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900 border-b border-slate-800">
              <span className="text-xs text-slate-400 flex items-center gap-1.5">
                <Globe className="h-3 w-3" /> {localUrl}
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-slate-400"
                      onClick={() => setIframeKey((k) => k + 1)}>
                <RefreshCw className="h-3 w-3" />
              </Button>
            </div>
            <iframe key={iframeKey} src={localUrl}
                    title={`${slug} live preview`}
                    referrerPolicy="strict-origin-when-cross-origin"
                    sandbox="allow-scripts allow-same-origin allow-popups"
                    style={{ width: '100%', height: 560, border: 0, background: '#fff' }} />
          </div>
        ) : (
          <div className="text-center py-6 border border-dashed border-slate-800 rounded text-xs text-slate-500">
            Set the public URL above and click <b>Re-check</b> to see the live preview here.
          </div>
        )}

        {/* Current payload */}
        {showPayload && payload && (
          <div className="border border-slate-800 rounded overflow-hidden">
            <div className="flex items-center justify-between px-3 py-1.5 bg-slate-900 border-b border-slate-800">
              <span className="text-xs text-slate-400">
                What the ecosystem is currently serving to <code>{PUBLIC_ENDPOINTS[slug]}</code>:
              </span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-slate-400"
                      onClick={() => setShowPayload(false)}>Hide</Button>
            </div>
            <pre className="text-[11px] text-emerald-300 bg-slate-950 p-3 overflow-auto max-h-72 whitespace-pre">
{JSON.stringify(payload, null, 2)}
            </pre>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const StatusPill = ({ label, ok }) => (
  <div className={`flex items-center gap-1.5 border rounded px-2 py-1.5 ${
    ok ? 'border-emerald-800/40 bg-emerald-900/10 text-emerald-300'
       : 'border-slate-800 bg-slate-800/20 text-slate-400'}`}>
    {ok ? <CheckCircle2 className="h-3 w-3" /> : <XCircle className="h-3 w-3" />}
    <span>{label}</span>
  </div>
);

const StatusBadge = ({ status }) => {
  if (!status) return <Badge variant="outline" className="text-slate-400">checking…</Badge>;
  if (!status.public_url) return (
    <Badge variant="outline" className="border-slate-700 text-slate-400">no URL set</Badge>);
  if (!status.reachable) return (
    <Badge variant="outline" className="border-rose-700 text-rose-300">unreachable</Badge>);
  if (status.embed_detected && status.cms_content_detected) return (
    <Badge variant="outline" className="border-emerald-700 text-emerald-300">fully connected</Badge>);
  if (status.embed_detected || status.cms_content_detected) return (
    <Badge variant="outline" className="border-amber-700 text-amber-300">partially connected</Badge>);
  return (
    <Badge variant="outline" className="border-slate-700 text-slate-400">standalone</Badge>);
};

const StatusIcon = ({ status }) => {
  if (!status || !status.public_url)
    return <Link2 className="h-4 w-4 text-slate-400" />;
  if (!status.reachable)
    return <XCircle className="h-4 w-4 text-rose-400" />;
  if (status.embed_detected && status.cms_content_detected)
    return <CheckCircle2 className="h-4 w-4 text-emerald-400" />;
  if (status.embed_detected || status.cms_content_detected)
    return <AlertTriangle className="h-4 w-4 text-amber-400" />;
  return <AlertTriangle className="h-4 w-4 text-slate-400" />;
};
